import React, { useState, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { getOr } from 'lodash/fp';
import useFormParams from '../../Hooks/useFormParams';
import {
  Hr, SearchSuggestions,
} from '../../Components/Auth/Layout';
import {
  FETCH_EVENT,
  FETCH_REGISTRATION_OPTIONS_BY_DEADLINE,
} from '../../GraphQL/Queries';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import Loader from '../../Components/Loader';
import GenericAlert from '../../Components/GenericAlert';
import useGraphQLErrorExtractor from '../../Hooks/useGraphQLErrorExtractor';
import useCurrentUser from '../../Hooks/userCurrentUser';
import Form, { PermissionButton, Input, RedirectButton } from '../../Components/Form';
import useUserPermissionChecker from '../../Hooks/userUserPermissionChecker';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import UserList from '../../Components/UserList/Index';
import DefaultImage from '../../Images/team-dum.png';
import REMOVE_ICON from '../../Images/cross.png';
import Search from '../../Components/Search/Index';
import { INVITE_EXITING_USER } from '../../GraphQL/Mutations';
import Permissions from '../../Constants/PermissionConstrants';

export const FETCH_UNJOIN_USERS = gql`
  query fetchUnregisteredUsers($userId: Int!, $eventId: Int!){
    fetchUnregisteredUsers(userId: $userId, eventId: $eventId){
      id
      firstname
      lastname
      code
      profileImageUrl
      fetchSchools { 
        name
      }
      status
    }
  }
`;

const InviteExistingUser = () => {
  const {
    currentUser,
    loading: userLoading,
    error: userError,
  } = useCurrentUser(true);
  const history = useHistory();
  const { eventId } = useParams();
  const { userType } = useParams();
  const checkPermission = useUserPermissionChecker(currentUser);
  const canInvite = useMemo(() => checkPermission(Permissions.CAN_INVITE_TEAM_MEMBER),
    [checkPermission]);
  const extractError = useGraphQLErrorExtractor();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchStringLength, setSearchStringLength] = useState(0);
  const [clickStatus, setClickStatus] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const { data: registrationOptionsData } = useQuery(FETCH_REGISTRATION_OPTIONS_BY_DEADLINE, {
    variables: {
      tenantId: currentUser?.tenantId,
      eventId: parseInt(eventId, 10),
    },
    skip: !currentUser || !eventId,
  });
  const registrations = registrationOptionsData?.registrationOptionByDeadline || [];

  const { data, loading, error } = useQuery(FETCH_EVENT, {
    variables: { id: parseInt(eventId, 10) },
  });
  const event = getOr({}, 'fetchEventDetail', data);

  const { data: unregisteredUsersData, loading: unjoinUsersLoading } = useQuery(
    FETCH_UNJOIN_USERS,
    {
      variables: {
        userId: parseInt(currentUser?.id, 10),
        eventId: parseInt(eventId, 10),
      },
      skip: !currentUser || !eventId || !canInvite,
    },
  );
  const totalUsers = unregisteredUsersData?.fetchUnregisteredUsers.filter(({ status }) => status === 'active') || [];

  useHeaderComponentsMutation({
    title: 'INVITE EXISITING USER(S)',
    backLink: `/event-manage/${eventId}`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const { params, onChange, setParams } = useFormParams({
  });

  const getSelectedUser = (obj) => {
    setClickStatus(false);
    setSearchStringLength(0);
    selectedUsers.push(obj);
    params[`userId_${selectedUsers.length - 1}`] = obj.id;
  };

  const searchUsers = (e) => {
    setSearchStringLength(e.target.value.length);
    setFilteredUsers(
      totalUsers.filter(
        (user) => ((`${user.firstname} ${user.lastname}`).toLowerCase()).includes(e.target.value.trimStart().toLowerCase()),
      ),
    );
  };

  const deleteUser = (user) => {
    const index = selectedUsers.indexOf(user);
    if (index !== -1) {
      selectedUsers.splice(index, 1);
    }
    setSelectedUsers([...selectedUsers]);
  };

  const [invitation, { loading: invitationLoading }] = useMutation(INVITE_EXITING_USER, {
    onCompleted: () => {
      history.push(`/event-manage/${parseInt(eventId, 10)}`);
    },
  });


  const onSubmit = (e) => {
    e.preventDefault();

    const invitations = [];
    const len = Object.values(params).length;
    for (let i = 0; i < len; i += 2) {
      invitations.push({
        userId: parseInt(Object.values(params)[i], 10),
        registrationOptionId: parseInt(Object.values(params)[i + 1], 10),
      });
    }
    params.invitations = invitations;
    invitation({
      variables: {
        ...params,
        invitableId: parseInt(eventId, 10),
        invitableType: 'Event',
      },
    });
  };

  if (loading || userLoading || unjoinUsersLoading) {
    return <Loader />;
  }
  if (error || userError) {
    return <GenericAlert>{extractError(error)}</GenericAlert>;
  }

  if (!canInvite) {
    return <Redirect to={`/event-manage/${eventId}`} />;
  }

  return (
    <>
      <div className="mt-4" />
      <div className="row">
        <div className="col-12 mx-auto px-4">
          <Search
            placeholder="Search"
            onChange={searchUsers}
          />
          {filteredUsers && searchStringLength > 0 && !clickStatus
            ? (
              <>
                {filteredUsers.map((obj) => (
                  <SearchSuggestions
                    onClick={() => getSelectedUser(obj)}
                    onChange={searchUsers}
                  >
                    <span id={obj.id}>
                      {`${obj.firstname} ${obj.lastname}`}
                    </span>
                  </SearchSuggestions>
                ))}
              </>
            )
            : null}
          <div className="mt-4" />
          <div className="mt-4" />
          <Hr />
          <Form onSubmit={onSubmit}>
            {selectedUsers.map((user, index) => (
              <>
                <div key={user.id}>
                  <UserList
                    user={user}
                    onClick={() => deleteUser(user)}
                    imageAttached={DefaultImage}
                    removeicon={REMOVE_ICON}
                    name={`userId_${index}`}
                    id={`userId_${index}`}
                    params={params}
                    setParams={setParams}
                  />
                  {(checkPermission(Permissions.CAN_INVITE_TEAM_MEMBER)
                   || checkPermission(Permissions.CAN_MANAGE_REGION))
                   && (userType === 'student' || userType === 'coach'
                   || userType === 'guest')
                    ? (
                      <>
                        <Input title="Registration Option" required type="select" params={params} onChange={onChange} name={`registrationOptionId_${index}`} id={`registrationOptionId_${index}`}>
                          <option value="" disabled>Select Registration Options</option>
                          {registrations && registrations.map((obj) => (
                            <option key={obj.id} value={obj.id}>{obj.title}</option>
                          ))}
                        </Input>
                        <div className="mt-4" />
                      </>
                    )
                    : null}
                  <Hr />
                </div>
              </>
            ))}
            <div className="mt-4" />
            <PermissionButton
              loading={invitationLoading}
              backgroundcolor="#F4AB37"
              fontColor="#FFF"
              border="none"
              htmltype="submit"
              checkPermission={(checkPermission(Permissions.CAN_INVITE_TEAM_MEMBER))}
            >
INVITE USER
            </PermissionButton>
          </Form>
          <RedirectButton backgroundcolor="#FFF" fontColor="rgba(244, 171, 55, 1)" border="2px solid #f3AA44" addNewText="INVITE NEW USER" addNewLink={`/event-manage/${eventId}/invite-new-user/student`} />
          <div className="mt-4" />
        </div>
      </div>
    </>
  );
};
export default InviteExistingUser;
